/**
 * Trang + bộ lọc của màn Khoản chi, được giữ trên URL search param.
 *
 * Config để ở đây (thay vì khai inline trong file route như các màn khác) để
 * giữ được bộ test đơn vị vốn đã có cho phần parse URL.
 */
import type {
    ExpensePaymentMethod,
    ExpensePaymentStatus,
} from '@/lib/api/expense-api'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { toggleChipFilter } from '@/components/common/filters/toggle-chip-filter'
import { useExpenseCategories } from '@/hooks/use-expense-category-query'
import { m } from '@/lib/paraglide/messages'

export type ExpensesSearch = {
    q?: string
    category_id?: string
    payment_method?: ExpensePaymentMethod
    payment_status?: ExpensePaymentStatus
    uncategorized?: 'all' | 'yes'
    possible_duplicate?: 'all' | 'yes'
    bank_sourced?: 'all' | 'yes'
    from_date?: string
    to_date?: string
    page?: number
}

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * URL là nguồn sự thật nên giá trị đến từ đây không đáng tin: người dùng có thể
 * sửa tay, hoặc link cũ mang theo giá trị đã bỏ. `options` của `selectFilter`
 * vừa là danh sách hiện ra vừa là tập giá trị hợp lệ nên giá trị lạ bị loại.
 */
export const EXPENSES_FILTERS = defineListFilters({
    q: textFilter({
        label: m.expense_search_label,
        placeholder: m.expense_vendor_name,
    }),
    category_id: remoteSelectFilter({
        label: m.expense_category_filter_label,
        allLabel: m.expense_filter_all_categories,
        useOptions: () => {
            const { data } = useExpenseCategories({
                is_active: true,
                per_page: 100,
            })
            return (data?.data ?? []).map((c) => ({
                value: c.id,
                label: c.name,
            }))
        },
    }),
    payment_method: selectFilter<'all' | ExpensePaymentMethod>({
        label: m.expense_payment_method,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.expense_filter_all_methods() },
            { value: 'cash', label: m.expense_payment_method_cash() },
            {
                value: 'bank_transfer',
                label: m.expense_payment_method_bank_transfer(),
            },
        ],
    }),
    payment_status: selectFilter<'all' | ExpensePaymentStatus>({
        label: m.expense_payment_status,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.expense_filter_all_status() },
            { value: 'paid', label: m.expense_payment_status_paid() },
            { value: 'unpaid', label: m.expense_payment_status_unpaid() },
        ],
    }),
    // Ba bộ lọc nhanh của HD-160: mỗi cái là một câu hỏi có/không độc lập
    // (kết hợp được với nhau), không phải một lựa chọn loại trừ nhau — nên
    // đứng dạng chip bấm được thay vì dropdown "Tất cả trạng thái" / giá trị
    // (kế toán đọc nhầm là phải chọn một trong hai). Vẫn cùng contract
    // 'all'/'yes' trên URL như trước.
    uncategorized: toggleChipFilter({
        label: m.expense_filter_uncategorized,
    }),
    possible_duplicate: toggleChipFilter({
        label: m.expense_filter_possible_duplicate,
    }),
    bank_sourced: toggleChipFilter({
        label: m.expense_filter_bank_sourced,
    }),
    from_date: dateFilter({ label: m.expense_filter_from }),
    to_date: dateFilter({ label: m.expense_filter_to }),
})
